// Single source of truth for how values are displayed. Locale is fixed to
// en-US/en-GB output so every user sees the same text; dates are shown in
// Jakarta time (the business timezone) regardless of the device zone.
//
//   date        03 Sep 2026
//   date+time   03 Sep 2026, 18:00
//   month       Sep 2026
//   number      1,234.56        (max 2 decimals, thousands separator)
//   currency    USD 1,234.56    (ISO code prefix, always 2 decimals)
//   percent     12.5%           (max 1 decimal)
//   duration    2d 3h 5m
//   file size   412 KB / 1.2 MB
//   empty       -

export const EMPTY_VALUE = '-';
export const DISPLAY_TIME_ZONE = 'Asia/Jakarta';

const isEmpty = value => value === null || value === undefined || value === '';

const toDate = value => {
  if (isEmpty(value)) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const partsOf = (date, options) =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone: DISPLAY_TIME_ZONE,
    hour12: false,
    ...options,
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

export const formatDate = (value, fallback = EMPTY_VALUE) => {
  const date = toDate(value);
  if (!date) return fallback;
  const p = partsOf(date, { day: '2-digit', month: 'short', year: 'numeric' });
  return `${p.day} ${p.month} ${p.year}`;
};

export const formatDateTime = (value, fallback = EMPTY_VALUE) => {
  const date = toDate(value);
  if (!date) return fallback;
  const p = partsOf(date, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute}`;
};

export const formatMonth = (value, fallback = EMPTY_VALUE) => {
  const date = toDate(value);
  if (!date) return fallback;
  const p = partsOf(date, { month: 'short', year: 'numeric' });
  return `${p.month} ${p.year}`;
};

export const formatDateRange = (start, end, fallback = EMPTY_VALUE) => {
  const from = formatDate(start, '');
  const to = formatDate(end, '');
  if (!from && !to) return fallback;
  if (from && to) return `${from} - ${to}`;
  return from || to;
};

const toNumber = value => {
  if (isEmpty(value)) return null;
  const number = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(number) ? number : null;
};

const decimalFormat = (min, max) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });

const numberFormat = decimalFormat(0, 2);
const currencyFormat = decimalFormat(2, 2);
const percentFormat = decimalFormat(0, 1);
const sizeFormat = decimalFormat(0, 1);

export const formatNumber = (value, fallback = EMPTY_VALUE) => {
  const number = toNumber(value);
  return number === null ? fallback : numberFormat.format(number);
};

export const formatInteger = (value, fallback = EMPTY_VALUE) => {
  const number = toNumber(value);
  return number === null ? fallback : decimalFormat(0, 0).format(number);
};

export const formatCurrency = (value, currency, fallback = EMPTY_VALUE) => {
  const number = toNumber(value);
  if (number === null) return fallback;
  const amount = currencyFormat.format(number);
  return currency ? `${currency} ${amount}` : amount;
};

// `value` is already a percentage (12.5 -> "12.5%"), not a 0-1 ratio.
export const formatPercent = (value, fallback = EMPTY_VALUE) => {
  const number = toNumber(value);
  return number === null ? fallback : `${percentFormat.format(number)}%`;
};

// Duration from a number of seconds -> "2d 3h 5m"; under a minute -> "45s".
export const formatDuration = (seconds, fallback = EMPTY_VALUE) => {
  const total = toNumber(seconds);
  if (total === null || total < 0) return fallback;
  const rounded = Math.round(total);
  if (rounded < 60) return `${rounded}s`;
  const days = Math.floor(rounded / 86400);
  const hours = Math.floor((rounded % 86400) / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  return [days && `${days}d`, hours && `${hours}h`, minutes && `${minutes}m`]
    .filter(Boolean)
    .join(' ');
};

export const formatFileSize = (bytes, fallback = EMPTY_VALUE) => {
  const size = toNumber(bytes);
  if (size === null || size <= 0) return fallback;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${sizeFormat.format(size / 1024)} KB`;
  return `${sizeFormat.format(size / (1024 * 1024))} MB`;
};

export const displayValue = (value, fallback = EMPTY_VALUE) =>
  isEmpty(value) ? fallback : value;
